/**
 * OAuth 2.0 + PKCE helpers (PRD FE-AUTH-02).
 *
 * state and the PKCE verifier are single-use and time-limited: they are stored
 * in sessionStorage under a one-time key, validated on the callback, and then
 * discarded. The return URL is restricted to safe in-site absolute paths.
 */

const STATE_PREFIX = "memos-oauth:";
const STATE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface StoredState {
  state: string;
  codeVerifier: string;
  idpName: string;
  returnUrl: string;
  createdAt: number;
  /** "login" starts a fresh session; "bind" attaches an identity to the current user. */
  mode: "login" | "bind";
}

function randomBytes(length: number): Uint8Array {
  const buf = new Uint8Array(length);
  crypto.getRandomValues(buf);
  return buf;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(input: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return new Uint8Array(digest);
}

/** Validate and normalise an in-site return URL. Rejects external/script URLs. */
export function safeReturnUrl(url: string | null | undefined, fallback = "/"): string {
  if (!url) return fallback;
  // Must be an in-site absolute path; reject protocol-relative and scheme URLs.
  if (!url.startsWith("/") || url.startsWith("//")) return fallback;
  const lower = url.toLowerCase();
  if (lower.startsWith("/\\") || lower.includes("javascript:") || lower.includes("data:")) return fallback;
  return url;
}

export function getOAuthRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

export interface BeginOAuthResult {
  authorizeUrl: string;
}

/**
 * Build the provider authorization URL and persist the one-time state/verifier.
 * `config` comes from the IdentityProvider's OAuth2Config.
 */
export async function beginOAuthFlow(input: {
  idpName: string;
  authUrl: string;
  clientId: string;
  scopes?: string[];
  returnUrl?: string;
  mode?: "login" | "bind";
}): Promise<string> {
  const state = base64UrlEncode(randomBytes(32));
  const codeVerifier = base64UrlEncode(randomBytes(64));
  const challenge = base64UrlEncode(await sha256(codeVerifier));

  const stored: StoredState = {
    state,
    codeVerifier,
    idpName: input.idpName,
    returnUrl: safeReturnUrl(input.returnUrl, "/"),
    createdAt: Date.now(),
    mode: input.mode ?? "login",
  };
  try {
    sessionStorage.setItem(STATE_PREFIX + state, JSON.stringify(stored));
  } catch {
    // sessionStorage may be unavailable (private mode); OAuth will fail closed.
  }

  const redirectUri = getOAuthRedirectUri();
  const url = new URL(input.authUrl);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", input.clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  const scopes = input.scopes && input.scopes.length > 0 ? input.scopes : ["openid", "profile", "email"];
  url.searchParams.set("scope", scopes.join(" "));
  return url.toString();
}

export interface ConsumeOAuthResult {
  idpName: string;
  code: string;
  codeVerifier: string;
  returnUrl: string;
  mode: "login" | "bind";
  redirectUri: string;
}

/**
 * Validate the callback's state & code, consume the stored one-time state, and
 * return everything needed to finish the sign-in. Throws on any mismatch.
 */
export function consumeOAuthCallback(params: { state?: string | null; code?: string | null }): ConsumeOAuthResult {
  const { state, code } = params;
  if (!state || !code) {
    throw new Error("Missing OAuth state or code.");
  }
  const raw = sessionStorage.getItem(STATE_PREFIX + state);
  // Always consume (single use) even on failure.
  sessionStorage.removeItem(STATE_PREFIX + state);
  if (!raw) {
    throw new Error("OAuth state not found or already used. Please try signing in again.");
  }
  let stored: StoredState;
  try {
    stored = JSON.parse(raw) as StoredState;
  } catch {
    throw new Error("Corrupted OAuth state.");
  }
  if (stored.state !== state) {
    throw new Error("OAuth state mismatch.");
  }
  if (Date.now() - stored.createdAt > STATE_TTL_MS) {
    throw new Error("OAuth sign-in link has expired. Please try again.");
  }
  return {
    idpName: stored.idpName,
    code,
    codeVerifier: stored.codeVerifier,
    returnUrl: safeReturnUrl(stored.returnUrl, "/"),
    mode: stored.mode,
    redirectUri: getOAuthRedirectUri(),
  };
}
